import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

// ==========================================================================
// SUBCOMPONENTE: COLUMNA DE JUGADOR
// Maneja su propia lógica de animación para el plato sin afectar al rival
// ==========================================================================
const PlayerColumn = ({ playerName, playerStats, avatarImg, isLeftColumn, customConfig }) => {
    const currentStep = playerStats?.currentStep || 1;
    const totalTacos = playerStats?.tacosCompleted || 0;

    // Estados para controlar la animación del plato
    const [plateCount, setPlateCount] = useState(0);
    const [isAnimatingPlate, setIsAnimatingPlate] = useState(false);
    const [pulseScore, setPulseScore] = useState(false);

    const ASSETS = {
        tacoBuilder: (step) => `/img/games/taco/${step}.png`,
        plate: (count) => `/img/games/taco/plato-${count}.png` // Asegúrate de tener plato-0.png hasta plato-5.png
    };

    // Efecto para vigilar cuando el jugador suma un taco
    useEffect(() => {
        // Hacemos latir el marcador gigante
        setPulseScore(true);
        setTimeout(() => setPulseScore(false), 300);

        // Lógica del plato
        const remainder = totalTacos % 5;

        if (totalTacos > 0 && remainder === 0) {
            // Acaba de completar un múltiplo de 5. Ponemos la imagen en 5 y animamos la salida.
            setPlateCount(5);
            setIsAnimatingPlate(true);

            // Medio segundo después, regresamos el plato vacío
            const timer = setTimeout(() => {
                setIsAnimatingPlate(false);
                setPlateCount(0);
            }, 600); 

            return () => clearTimeout(timer);
        } else {
            // Si no es múltiplo de 5, simplemente mostramos el progreso actual
            setPlateCount(remainder);
            setIsAnimatingPlate(false);
        }
    }, [totalTacos]);

    return (
        <div className="taco-screen__player-column">
            
            {/* 1. EL AVATAR */}
            <div className="taco-screen__avatar-container">
                <img src={avatarImg} alt={playerName} className="taco-screen__avatar" />
                <h2 className="taco-screen__name">{playerName}</h2>
            </div>

            {/* 2. TACO EN CONSTRUCCIÓN (Ingredientes) */}
            <div className="taco-screen__builder">
                <img 
                    src={customConfig?.foodSteps?.[currentStep - 1] ? `${API_URL.replace('/api/', '')}${customConfig.foodSteps[currentStep - 1]}` : ASSETS.tacoBuilder(currentStep)} 
                    alt={`Paso ${currentStep}`} 
                    className="taco-screen__taco-img" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* 3. MARCADOR GIGANTE */}
            <div className="taco-screen__score-box">
                <p className={`taco-screen__score-number ${pulseScore ? 'taco-screen__score-number--pulse' : ''}`}>
                    {totalTacos}
                </p>
                <p className="taco-screen__score-label">{customConfig?.foodNamePlural || "Tacos"}</p>
            </div>

            {/* 4. EL PLATO QUE SE LLENA */}
            <div className="taco-screen__plate-container">
                <img 
                    src={customConfig?.plateSteps?.[plateCount] ? `${API_URL.replace('/api/', '')}${customConfig.plateSteps[plateCount]}` : ASSETS.plate(plateCount)} 
                    alt={`Plato con ${plateCount} ${customConfig?.foodNamePlural || "tacos"}`} 
                    className={`taco-screen__plate-img ${isAnimatingPlate ? (isLeftColumn ? 'taco-screen__plate-img--slide-left' : 'taco-screen__plate-img--slide-right') : ''}`} 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

        </div>
    );
};


// ==========================================================================
// COMPONENTE PRINCIPAL DE PANTALLA GIGANTE
// ==========================================================================
const Tacos = ({ currentGameState }) => {
    
    // Extraemos 'endsAt' de la data
    const {
        status = 0, 
        winMode = 0,
        durationSeconds = 20,
        endsAt = null, // NUEVO: Extraemos la hora exacta de fin
        player1 = null,
        player2 = null,
        winner = 'none',
        customConfig = null
    } = currentGameState || {};

    const [timeLeft, setTimeLeft] = useState(durationSeconds);

    // Reloj Local de la Pantalla (Sincronizado Absolutamente)
    useEffect(() => {
        let timer;
        
        if (status === 1) {
            if (winMode === 0 && endsAt) {
                // Sincronización Estricta
                const endTimeMs = new Date(endsAt).getTime();
                
                timer = setInterval(() => {
                    const remainingMs = endTimeMs - Date.now();
                    
                    if (remainingMs <= 0) {
                        setTimeLeft(0);
                        clearInterval(timer); // Apagamos el reloj justo en el 0
                    } else {
                        setTimeLeft(Math.ceil(remainingMs / 1000));
                    }
                }, 200); // 5 veces por segundo para evitar el desfase visual
            }
        } else if (status === 0) {
            setTimeLeft(durationSeconds);
        }
        
        return () => clearInterval(timer);
    }, [status, durationSeconds, winMode, endsAt]);

    // Traductor de mensajes finales
    const getWinnerText = () => {
        if (winner === 'tie') return '¡ES UN EMPATE!';
        if (winner === 'player1') return `¡GANÓ ${customConfig?.player1Name?.toUpperCase() || 'XOLO MAYOR ROJO'}!`;
        if (winner === 'player2') return `¡GANÓ ${customConfig?.player2Name?.toUpperCase() || 'XOLO MAYOR BLANCO'}!`;
        return '';
    };

    const hasCustomBg = Boolean(customConfig?.gameBackground);
    const screenStyle = hasCustomBg ? {
        backgroundImage: `url(${API_URL.replace('/api/', '')}${customConfig.gameBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
    } : {};

    return (
        <div className="taco-screen" style={screenStyle}>
            <div className="taco-screen__overlay-bg"></div>

            {/* RELOJ CENTRAL */}
            {winMode === 0 && (
                <div className="taco-screen__hud">
                    <img 
                        src={customConfig?.coverLogo ? `${API_URL.replace('/api/', '')}${customConfig.coverLogo}` : "/img/page/xolos-five.png"} 
                        className="LogoADSLxZtudia" 
                        alt="Logo" 
                        style={{ width: '450px', maxWidth: '450px' }} 
                    />
                </div>
            )}
            
            {winMode === 1 && (
                <div className="taco-screen__hud">
                    <p className="taco-screen__hud-title text-danger">Muerte Súbita</p>
                    <p className="taco-screen__hud-time" style={{ fontSize: '2rem' }}>¡El Primero en llegar!</p>
                </div>
            )}

            {/* ARENA 1 VS 1 */}
            <div className="taco-screen__arena">
                {/* JUGADOR 1 (Izquierda) */}
                <PlayerColumn 
                    playerName={customConfig?.player1Name || "Xolo Mayor Rojo"}
                    playerStats={player1} 
                    avatarImg={customConfig?.player1Avatar ? `${API_URL.replace('/api/', '')}${customConfig.player1Avatar}` : "/img/games/taco/xolo-caricatura-rojo.png"} 
                    isLeftColumn={true}
                    customConfig={customConfig}
                />

                {/* JUGADOR 2 (Derecha) */}
                <PlayerColumn 
                    playerName={customConfig?.player2Name || "Xolo Mayor Blanco"}
                    playerStats={player2} 
                    avatarImg={customConfig?.player2Avatar ? `${API_URL.replace('/api/', '')}${customConfig.player2Avatar}` : "/img/games/taco/xolo-caricatura-blanco.png"} 
                    isLeftColumn={false}
                    customConfig={customConfig}
                />
            </div>

            {/* BANNER DE VICTORIA GIGANTE */}
            {status === 2 && winner !== 'none' && (
                <div className="taco-screen__winner-banner">
                    <img 
                        src={customConfig?.coverLogo ? `${API_URL.replace('/api/', '')}${customConfig.coverLogo}` : "/img/page/xolos-five.png"} 
                        className="LogoADSLxZtudia" 
                        alt="Logo" 
                        style={{ width: '450px', maxWidth: '450px' }} 
                    />
                    <h1>{getWinnerText()}</h1>
                </div>
            )}
        </div>
    );
};

export { Tacos };











// ==========================================================================
// COMPONENTE PRINCIPAL DE PANTALLA GIGANTE
// ==========================================================================
// const Tacos = ({ currentGameState }) => {
    
//     // Extracción segura de la data inyectada por Screen.js
//     const {
//         status = 0, // 0: Waiting, 1: Running, 2: Finished
//         winMode = 0,
//         durationSeconds = 20,
//         player1 = null,
//         player2 = null,
//         winner = 'none'
//     } = currentGameState || {};

//     const [timeLeft, setTimeLeft] = useState(durationSeconds);

//     // Reloj Local de la Pantalla (Sincronizado)
//     useEffect(() => {
//         let timer;
//         // Si arranca un juego nuevo, reseteamos el reloj
//         if (status === 1) {
//             setLocalTimeLeftSafe();
            
//             // Solo corremos el reloj si el modo de victoria es por Tiempo (0)
//             if (winMode === 0) {
//                 timer = setInterval(() => {
//                     setTimeLeft(prev => Math.max(0, prev - 1));
//                 }, 1000);
//             }
//         } else if (status === 0) {
//             setTimeLeft(durationSeconds); // Espera: muestra el tiempo inicial
//         }
        
//         return () => clearInterval(timer);
//     }, [status, durationSeconds, winMode]);

//     const setLocalTimeLeftSafe = () => {
//         setTimeLeft(prev => prev === 0 ? durationSeconds : prev);
//     }

//     // Traductor de mensajes finales
//     const getWinnerText = () => {
//         if (winner === 'tie') return '¡ES UN EMPATE!';
//         if (winner === 'player1') return '¡GANÓ LUCHO!';
//         if (winner === 'player2') return '¡GANÓ TUNITA!';
//         return '';
//     };

//     return (
//         <div className="taco-screen">
//             <div className="taco-screen__overlay-bg"></div>

//             {/* RELOJ CENTRAL */}
//             {winMode === 0 && (
//                 <div className="taco-screen__hud">
//                     <p className="taco-screen__hud-title">Tiempo</p>
//                     <p className="taco-screen__hud-time">00:{timeLeft.toString().padStart(2, '0')}</p>
//                 </div>
//             )}
            
//             {winMode === 1 && (
//                 <div className="taco-screen__hud">
//                     <p className="taco-screen__hud-title text-danger">Muerte Súbita</p>
//                     <p className="taco-screen__hud-time" style={{ fontSize: '2rem' }}>¡El Primero en llegar!</p>
//                 </div>
//             )}

//             {/* ARENA 1 VS 1 */}
//             <div className="taco-screen__arena">
                
//                 {/* JUGADOR 1 (Izquierda) */}
//                 <PlayerColumn 
//                     playerName="Lucho" 
//                     playerStats={player1} 
//                     avatarImg="/img/games/taco/lucho-taquero.png" 
//                     isLeftColumn={true}
//                 />

//                 {/* JUGADOR 2 (Derecha) */}
//                 <PlayerColumn 
//                     playerName="Tunita" 
//                     playerStats={player2} 
//                     avatarImg="/img/games/taco/tunita-taquera.png" 
//                     isLeftColumn={false}
//                 />

//             </div>

//             {/* BANNER DE VICTORIA GIGANTE */}
//             {status === 2 && winner !== 'none' && (
//                 <div className="taco-screen__winner-banner">
//                     <h1>{getWinnerText()}</h1>
//                 </div>
//             )}
//         </div>
//     );
// };

// export { Tacos };